import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, RefreshCw, List, X } from 'lucide-react';

const BOM = () => {
  const { api, isAdmin, hasRole } = useAuth();
  const [data, setData] = useState([]);
  const [parts, setParts] = useState([]);
  const [items, setItems] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedPartId, setSelectedPartId] = useState('');
  const [bomLines, setBomLines] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canEdit = isAdmin() || hasRole(['ADMIN', 'PRODUCTION']);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const bomRes = await api.get('/bom');
      setData(bomRes.data);
      setLoading(false);
      const [partsRes, itemsRes, uomsRes] = await Promise.all([
        api.get('/parts'), api.get('/items'), api.get('/uoms')
      ]);
      setParts(partsRes.data);
      setItems(itemsRes.data);
      setUoms(uomsRes.data);
    } catch { toast.error('Failed to fetch data'); setLoading(false); }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getPartName = (id) => { const p = parts.find(x => x.id === id); return p ? `${p.part_no} - ${p.name}` : `ID:${id}`; };
  const getItemName = (id) => { const i = items.find(x => x.id === id); return i ? `${i.code} - ${i.name}` : `ID:${id}`; };
  const getUomName = (id) => { if (!id) return '-'; const u = uoms.find(x => x.id === id); return u ? u.code : '-'; };

  // Group BOM by part for display
  const groupedByPart = data.reduce((acc, bom) => {
    if (!acc[bom.part_id]) acc[bom.part_id] = [];
    acc[bom.part_id].push(bom);
    return acc;
  }, {});

  const handleOpenDialog = (item = null) => {
    if (item) {
      // Edit single BOM entry
      setEditingItem(item);
      setSelectedPartId((item.part_id ? item.part_id.toString() : ''));
      setBomLines([{
        id: item.id,
        item_id: (item.item_id ? item.item_id.toString() : ''),
        quantity: item.quantity || '',
        uom_id: (item.uom_id ? item.uom_id.toString() : ''),
        sequence: item.sequence || '',
        remarks: item.remarks || '',
        is_active: item.is_active !== false,
      }]);
    } else {
      setEditingItem(null);
      setSelectedPartId('');
      setBomLines([]);
    }
    setIsDialogOpen(true);
  };

  // #4: Open dialog to add/edit multiple items for a part
  const handleOpenPartBOM = (partId) => {
    setEditingItem(null);
    setSelectedPartId(partId.toString());
    const existing = data.filter(b => b.part_id === partId);
    if (existing.length > 0) {
      setBomLines(existing.map(b => ({
        id: b.id, item_id: (b.item_id ? b.item_id.toString() : ''), quantity: b.quantity || '',
        uom_id: (b.uom_id ? b.uom_id.toString() : ''), sequence: b.sequence || '',
        remarks: b.remarks || '', is_active: b.is_active !== false,
      })));
    } else {
      setBomLines([{ item_id: '', quantity: '', uom_id: '', sequence: '', remarks: '', is_active: true }]);
    }
    setIsDialogOpen(true);
  };

  const handleAddLine = () => {
    setBomLines([...bomLines, { item_id: '', quantity: '', uom_id: '', sequence: '', remarks: '', is_active: true }]);
  };

  const handleLineChange = (index, field, value) => {
    const updated = [...bomLines];
    updated[index] = { ...updated[index], [field]: value };
    setBomLines(updated);
  };

  const handleRemoveLine = (index) => setBomLines(bomLines.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPartId) { toast.error('Please select a Part'); return; }
    const validLines = bomLines.filter(l => l.item_id && l.quantity);
    if (validLines.length === 0) { toast.error('At least one item is required'); return; }
    
    setIsSubmitting(true);
    try {
      if (editingItem) {
        // Update single entry
        const line = validLines[0];
        await api.put(`/bom/${editingItem.id}`, {
          part_id: parseInt(selectedPartId),
          item_id: parseInt(line.item_id),
          quantity: parseFloat(line.quantity),
          uom_id: line.uom_id ? parseInt(line.uom_id) : null,
          sequence: line.sequence ? parseInt(line.sequence) : null,
          remarks: line.remarks || null,
          is_active: line.is_active,
        });
        toast.success('BOM entry updated');
      } else {
        // Create multiple entries
        for (const line of validLines) {
          await api.post('/bom', {
            part_id: parseInt(selectedPartId),
            item_id: parseInt(line.item_id),
            quantity: parseFloat(line.quantity),
            uom_id: line.uom_id ? parseInt(line.uom_id) : null,
            sequence: line.sequence ? parseInt(line.sequence) : null,
            remarks: line.remarks || null,
            is_active: true,
          });
        }
        toast.success(`${validLines.length} BOM entries created`);
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error((error.response && error.response.data && error.response.data.detail) || 'Operation failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Delete this BOM entry?')) return;
    try { await api.delete(`/bom/${item.id}`); toast.success('Deleted'); fetchData(); }
    catch (error) { toast.error((error.response && error.response.data && error.response.data.detail) || 'Delete failed'); }
  };

  const filteredParts = Object.keys(groupedByPart).filter(partId => {
    if (!searchTerm) return true;
    return getPartName(parseInt(partId)).toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div className="p-6 max-w-[1600px] mx-auto animate-fade-in" data-testid="bom">
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2"><List className="w-6 h-6 text-sky-500" /> Bill of Materials</h1>
          <p className="text-slate-500 text-sm">{data.length} entries across {Object.keys(groupedByPart).length} parts</p>
        </div>
        {canEdit && (
          <Button onClick={() => handleOpenDialog()} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="add-new-btn">
            <Plus className="w-4 h-4 mr-2" /> Add BOM
          </Button>
        )}
      </div>

      <Card className="mb-4 border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search part..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 rounded-sm" />
            </div>
            <Button variant="outline" size="icon" onClick={fetchData} className="rounded-sm"><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      {/* Grouped by Part */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><div className="spinner" /></div>
      ) : filteredParts.length === 0 ? (
        <div className="text-center py-12 text-slate-500">No BOM data</div>
      ) : (
        <div className="space-y-4">
          {filteredParts.map(partId => {
            const pid = parseInt(partId);
            const entries = groupedByPart[pid];
            return (
              <Card key={pid} className="border-slate-200">
                <CardContent className="p-0">
                  <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b">
                    <h3 className="font-semibold text-sm">{getPartName(pid)} <span className="text-slate-400 ml-2">{entries.length} materials</span></h3>
                    {canEdit && (
                      <Button variant="outline" size="sm" onClick={() => handleOpenPartBOM(pid)} className="rounded-sm h-7 text-xs" data-testid={`edit-bom-${pid}`}>
                        <Pencil className="w-3 h-3 mr-1" /> Edit BOM
                      </Button>
                    )}
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Seq</TableHead>
                        <TableHead>Item (Material)</TableHead>
                        <TableHead>UOM</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead>Remarks</TableHead>
                        {canEdit && <TableHead className="text-right">Actions</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {entries.map(bom => (
                        <TableRow key={bom.id}>
                          <TableCell className="font-mono">{bom.sequence || '-'}</TableCell>
                          <TableCell>{getItemName(bom.item_id)}</TableCell>
                          <TableCell>{getUomName(bom.uom_id)}</TableCell>
                          <TableCell className="text-right font-mono">{parseFloat(bom.quantity).toFixed(4)}</TableCell>
                          <TableCell className="text-sm text-slate-500">{bom.remarks || '-'}</TableCell>
                          {canEdit && (
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(bom)} className="h-7 w-7"><Pencil className="w-3 h-3" /></Button>
                                <Button variant="ghost" size="icon" onClick={() => handleDelete(bom)} className="h-7 w-7 text-red-600"><Trash2 className="w-3 h-3" /></Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog - supports multiple lines */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit BOM Entry' : 'Add BOM Entries'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <Label className="form-label">Part <span className="text-red-500">*</span></Label>
              <Select value={selectedPartId} onValueChange={setSelectedPartId} disabled={!!editingItem}>
                <SelectTrigger className="rounded-sm"><SelectValue placeholder="Select Part" /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {parts.filter(p => p.status === 'ACTIVE').map(p => (
                    <SelectItem key={p.id} value={p.id.toString()}>{p.part_no} - {p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-sm">Materials</h3>
              {!editingItem && (
                <Button type="button" variant="outline" size="sm" onClick={handleAddLine} className="rounded-sm">
                  <Plus className="w-3 h-3 mr-1" /> Add Material
                </Button>
              )}
            </div>

            <div className="border rounded-sm overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Item (Material)</TableHead>
                    <TableHead>UOM</TableHead>
                    <TableHead>Qty</TableHead>
                    <TableHead>Seq</TableHead>
                    <TableHead>Remarks</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bomLines.map((line, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Select value={line.item_id} onValueChange={(v) => handleLineChange(i, 'item_id', v)}>
                          <SelectTrigger className="rounded-sm w-48"><SelectValue placeholder="Select Item" /></SelectTrigger>
                          <SelectContent className="max-h-60">
                            {items.filter(it => it.is_active).map(it => (
                              <SelectItem key={it.id} value={it.id.toString()}>{it.code} - {it.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select value={line.uom_id || undefined} onValueChange={(v) => handleLineChange(i, 'uom_id', v)}>
                          <SelectTrigger className="rounded-sm w-20"><SelectValue placeholder="UOM" /></SelectTrigger>
                          <SelectContent>
                            {uoms.map(u => <SelectItem key={u.id} value={u.id.toString()}>{u.code}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell><Input type="number" step="0.0001" value={line.quantity} onChange={(e) => handleLineChange(i, 'quantity', e.target.value)} className="rounded-sm w-24" /></TableCell>
                      <TableCell><Input type="number" value={line.sequence} onChange={(e) => handleLineChange(i, 'sequence', e.target.value)} className="rounded-sm w-16" /></TableCell>
                      <TableCell><Input value={line.remarks || ''} onChange={(e) => handleLineChange(i, 'remarks', e.target.value)} className="rounded-sm w-32" /></TableCell>
                      <TableCell>
                        {!editingItem && bomLines.length > 1 && (
                          <Button type="button" variant="ghost" size="icon" onClick={() => handleRemoveLine(i)} className="text-red-600 h-7 w-7"><X className="w-3 h-3" /></Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" disabled={isSubmitting} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="submit-bom">
                {isSubmitting ? 'Saving...' : editingItem ? 'Update' : `Create ${bomLines.filter(l => l.item_id && l.quantity).length} Entries`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BOM;
